import React from 'react';
import { Dialog, DialogContent, Box, Typography, Button } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';

interface Security2FARequiredModalProps {
  open: boolean;
  onClose: () => void;
  onSetup2FA: () => void;
  feature?: string;
}

const Security2FARequiredModal: React.FC<Security2FARequiredModalProps> = ({
  open,
  onClose,
  onSetup2FA,
  feature = 'this feature',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: '460px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header with gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <SecurityIcon sx={{ fontSize: 44, color: 'white' }} />
          </Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: 'white', mb: 1 }}>
            Two-Factor Authentication Required
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
            Security compliance requirement
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, pt: 3 }}>
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 3, textAlign: 'center' }}>
            To access <strong>{feature}</strong>, you need to enable Two-Factor Authentication (2FA) on your account. This helps protect your data and ensures compliance with security requirements.
          </Typography>

          {/* Security benefits */}
          <Box sx={{ bgcolor: '#FEF3C7', borderRadius: '12px', p: 2.5, mb: 3, border: '1px solid #FDE68A' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#92400E', mb: 1.5 }}>
              Why is 2FA required?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                'Protects your account from unauthorized access',
                'Required for workspace and resource management',
                'Ensures compliance with security best practices',
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LockIcon sx={{ fontSize: 14, color: '#D97706' }} />
                  <Typography sx={{ fontSize: '12px', color: '#78350F' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onClose}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                borderColor: '#D5D7DA',
                color: '#535862',
                fontWeight: 600,
                py: 1.25,
                '&:hover': { borderColor: '#9CA3AF' },
              }}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={onSetup2FA}
              startIcon={<SecurityIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
                fontWeight: 600,
                py: 1.25,
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #D97706 0%, #DC2626 100%)',
                },
              }}
            >
              Set Up 2FA Now
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Security2FARequiredModal;
